import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MergeIcon from '@mui/icons-material/MergeType';
import { IUnitSelection } from '../../../../../services/unitsMapping';
import { T_KEYS } from '../../../constants';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '480px',
    width: '100%',
    padding: '0',
  },
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid #f0f0f0',
});

const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#262626',
});

const Content = styled(Box)({
  padding: '24px',
});

const IconContainer = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: '#f3f0ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
});

const Message = styled(Typography)({
  fontSize: '14px',
  color: '#666666',
  textAlign: 'center',
  marginBottom: '24px',
  lineHeight: '22px',
});

const SelectedUnitsContainer = styled(Box)({
  backgroundColor: '#fafafa',
  borderRadius: '12px',
  padding: '16px',
  maxHeight: '200px',
  overflowY: 'auto',
});

const UnitItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const UnitImage = styled(Avatar)({
  width: '36px',
  height: '36px',
  borderRadius: '6px',
});

const UnitInfo = styled(Box)({
  flex: 1,
});

const UnitName = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#262626',
});

const ChannelBadge = styled(Box)<{ bgColor: string }>(({ bgColor }) => ({
  padding: '2px 8px',
  borderRadius: '4px',
  backgroundColor: bgColor,
  color: '#ffffff',
  fontSize: '11px',
  fontWeight: 600,
}));

const Actions = styled(DialogActions)({
  padding: '16px 24px 24px',
  gap: '12px',
});

const CancelButton = styled(Button)({
  flex: 1,
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#666666',
  backgroundColor: '#f5f5f5',
  '&:hover': {
    backgroundColor: '#e8e8e8',
  },
});

const ConfirmButton = styled(Button)({
  flex: 1,
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#ffffff',
  backgroundColor: '#5b3fff',
  '&:hover': {
    backgroundColor: '#4a2fe8',
  },
});

interface MergeConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedUnits: IUnitSelection[];
  isLoading?: boolean;
}

const getChannelColor = (channel: string): string => {
  switch (channel) {
    case 'airbnb':
      return '#ff5a5f';
    case 'booking':
      return '#003580';
    case 'gathern':
      return '#1a73e8';
    default:
      return '#666666';
  }
};

const getChannelLabel = (channel: string): string => {
  switch (channel) {
    case 'airbnb':
      return 'Airbnb';
    case 'booking':
      return 'Booking';
    case 'gathern':
      return 'Gathern';
    default:
      return channel;
  }
};

const MergeConfirmModal: React.FC<MergeConfirmModalProps> = memo(({
  open,
  onClose,
  onConfirm,
  selectedUnits,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <StyledDialog open={open} onClose={onClose}>
      <Header>
        <HeaderTitle>
          {t(T_KEYS.UNITS_MAPPING_MERGE_TITLE)}
        </HeaderTitle>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#666666' }} />
        </IconButton>
      </Header>

      <Content>
        <IconContainer>
          <MergeIcon sx={{ fontSize: 28, color: '#5b3fff' }} />
        </IconContainer>

        <Message>
          {t(T_KEYS.UNITS_MAPPING_MERGE_MSG)}
        </Message>

        <SelectedUnitsContainer>
          {selectedUnits.map((unit, index) => (
            <UnitItem key={`${unit.unitId}-${unit.channel}-${index}`}>
              <UnitImage
                src={unit.image}
                alt={unit.name}
                variant="rounded"
              >
                {unit.name?.charAt(0) || '?'}
              </UnitImage>
              <UnitInfo>
                <UnitName>{unit.name}</UnitName>
              </UnitInfo>
              <ChannelBadge bgColor={getChannelColor(unit.channel)}>
                {getChannelLabel(unit.channel)}
              </ChannelBadge>
            </UnitItem>
          ))}
        </SelectedUnitsContainer>
      </Content>

      <Actions>
        <CancelButton onClick={onClose} disabled={isLoading}>
          {t(T_KEYS.UNITS_MAPPING_MERGE_NO)}
        </CancelButton>
        <ConfirmButton onClick={onConfirm} disabled={isLoading}>
          {isLoading 
            ? t(T_KEYS.COMMON_LOADING)
            : t(T_KEYS.UNITS_MAPPING_MERGE_YES)
          }
        </ConfirmButton>
      </Actions>
    </StyledDialog>
  );
});

MergeConfirmModal.displayName = 'MergeConfirmModal';

export default MergeConfirmModal;
